"use client";

import React, { useState, useEffect } from "react";
import { Leaf, Menu, Phone, X } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
import { cn } from "@/lib/utils";
function HeaderMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div data-section-id="104610" className="hidden md:flex gap-4">
        {[1, 2, 3, 4].map(i => <div key={i} className="h-6 w-20 bg-muted animate-pulse rounded-md" />)}
      </div>;
  }
  return <NavigationMenu className="hidden md:flex" viewport={false}>
      <NavigationMenuList className="gap-2">
        {menuItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
            <NavigationMenuLink asChild>
              <Link to={item.href || "#"} className="group relative px-3 py-2 text-sm font-medium text-foreground transition-colors hover:text-secondary">
                {item.label}
                <span className="absolute bottom-0 left-0 h-[2px] w-full origin-bottom-right scale-x-0 bg-secondary transition-transform duration-300 ease-out group-hover:origin-bottom-left group-hover:scale-x-100" />
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileMenuConsumer({
  isOpen,
  onClose
}: {
  isOpen: boolean;
  onClose: () => void;
}) {
  const {
    menuItems
  } = useMenu();
  if (!isOpen) return null;
  return <div className="absolute top-full left-0 w-full bg-background border-b border-border shadow-lg md:hidden animate-in slide-in-from-top-2">
      <div className="flex flex-col px-4 py-6 gap-4">
        {menuItems.map((item, i) => <Link key={item.id} to={item.href || "#"} className="text-lg font-medium text-foreground py-2 border-b border-border/50 hover:text-secondary transition-colors" onClick={onClose} data-index={i}>
            {item.label}
          </Link>)}
        <Button className="w-full mt-4 bg-accent text-accent-foreground hover:bg-accent/90" size="lg">
          <Phone className="size-5 animate-pulse mr-2" />
          Your Phone Number Here
        </Button>
      </div>
    </div>;
}
export default function VilHeader() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        if (href === "/#") {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: "smooth"
          });
          return;
        }
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (hashPart === "") {
            e.preventDefault();
            window.scrollTo({
              top: 0,
              behavior: "smooth"
            });
            return;
          }
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
            setIsMobileMenuOpen(false);
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  return <header id="site-header" className="sticky top-0 z-50 w-full transition-all duration-500 ease-in-out bg-background py-5 !h-[113.925px] md:py-6">
      <div className="container mx-auto px-4 md:px-6">
        <div className="flex items-center justify-between !h-[125.925px]">
          {}
          <div className="flex-shrink-0 z-10">
            <Link to="/" className="flex items-center gap-2">
              <img src="https://landscaperswebsites.com/wp-content/uploads/2026/04/ChatGPT-Image-Apr-5-2026-04_25_59-PM.png" alt="Nieser Landscaping" className="!w-[246.964px] !h-[152.928px]" />
            </Link>
          </div>

          {}
          <div className="hidden md:flex flex-1 justify-center px-8">
            <MenuProvider menu_id="16">
              <HeaderMenuConsumer />
            </MenuProvider>
          </div>

          {}
          <div className="flex items-center gap-4 z-10">
            <Button className="hidden sm:flex bg-accent text-accent-foreground hover:bg-accent/90 shadow-md transition-all hover:shadow-lg" size={isScrolled ? "default" : "lg"}><svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" className="lucide lucide-phone size-4 animate-pulse mr-2" aria-hidden="true" data-ast-id="f:/src/reusable_sections/Header.tsx|sig:xm04xu" data-ast-start="5417" data-ast-end="5464"><path d="M13.832 16.568a1 1 0 0 0 1.213-.303l.355-.465A2 2 0 0 1 17 15h3a2 2 0 0 1 2 2v3a2 2 0 0 1-2 2A18 18 0 0 1 2 4a2 2 0 0 1 2-2h3a2 2 0 0 1 2 2v3a2 2 0 0 1-.8 1.6l-.468.351a1 1 0 0 0-.292 1.233 14 14 0 0 0 6.392 6.384"></path></svg><span className="font-semibold tracking-wide" data-ast-id="f:/src/reusable_sections/Header.tsx|sig:pnms06" data-ast-start="5479" data-ast-end="5554">Give us a Call</span></Button>

            {}
            <Button variant="ghost" size="icon" className="md:hidden text-foreground" onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} aria-label="Toggle menu">
              {isMobileMenuOpen ? <X className="size-6" /> : <Menu className="size-6" />}
            </Button>
          </div>
        </div>
      </div>

      {}
      <MenuProvider menu_id="16">
        <MobileMenuConsumer isOpen={isMobileMenuOpen} onClose={() => setIsMobileMenuOpen(false)} />
      </MenuProvider>
    </header>;
}